import { validCountryCodes } from "~/shared/constants/country.constants";
import { REGION_CODES, type RegionCountry, regionByName, regionsForCode } from "~/shared/utils/region-codes";

/**
 * Maps common free-text country aliases to ISO 3166-1 alpha-2 codes.
 * Used as a safety net at write boundaries and in import/programmatic paths.
 * The UI uses a structured CountrySelect, so this primarily catches legacy data and CSV imports.
 * These aliases are also part of the frozen v1 Public API receipt identity; add future aliases separately.
 */
const COUNTRY_ALIAS_TO_ISO2: Record<string, string> = {
  // United States
  US: "US",
  USA: "US",
  "U.S.": "US",
  "U.S.A.": "US",
  "UNITED STATES": "US",
  "UNITED STATES OF AMERICA": "US",
  // Canada
  CA: "CA",
  CAN: "CA",
  CANADA: "CA",
  // Mexico
  MX: "MX",
  MEX: "MX",
  MEXICO: "MX",
  // United Kingdom
  GB: "GB",
  UK: "GB",
  GBR: "GB",
  "UNITED KINGDOM": "GB",
  ENGLAND: "GB",
  "GREAT BRITAIN": "GB",
  // Germany
  DE: "DE",
  DEU: "DE",
  GERMANY: "DE",
  DEUTSCHLAND: "DE",
  // France
  FR: "FR",
  FRA: "FR",
  FRANCE: "FR",
  // Japan
  JP: "JP",
  JPN: "JP",
  JAPAN: "JP",
  // China
  CN: "CN",
  CHN: "CN",
  CHINA: "CN",
  // India
  IN: "IN",
  IND: "IN",
  INDIA: "IN",
  // Brazil
  BR: "BR",
  BRA: "BR",
  BRAZIL: "BR",
  // Australia
  AU: "AU",
  AUS: "AU",
  AUSTRALIA: "AU",
  // South Korea
  KR: "KR",
  KOR: "KR",
  "SOUTH KOREA": "KR",
  "KOREA (SOUTH)": "KR",
  // Italy
  IT: "IT",
  ITA: "IT",
  ITALY: "IT",
  // Netherlands
  NL: "NL",
  NLD: "NL",
  NETHERLANDS: "NL",
  HOLLAND: "NL",
  // Spain
  ES: "ES",
  ESP: "ES",
  SPAIN: "ES",
  // Switzerland
  CH: "CH",
  CHE: "CH",
  SWITZERLAND: "CH",
  // Belgium
  BE: "BE",
  BEL: "BE",
  BELGIUM: "BE",
  // Taiwan
  TW: "TW",
  TWN: "TW",
  TAIWAN: "TW",
  // Singapore
  SG: "SG",
  SGP: "SG",
  SINGAPORE: "SG",
  // Israel
  IL: "IL",
  ISR: "IL",
  ISRAEL: "IL",
  // Ireland
  IE: "IE",
  IRL: "IE",
  IRELAND: "IE",
};

const normalizeRawString = (value: unknown): string | null | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
};

/**
 * Normalizes common country aliases into ISO 3166-1 alpha-2 values when known.
 * Falls through to the original value when no alias match is found.
 */
export const normalizeCountry = (value: unknown): string | null | undefined => {
  const normalized = normalizeRawString(value);
  if (normalized == null) {
    return normalized;
  }

  const uppercase = normalized.toUpperCase();
  return COUNTRY_ALIAS_TO_ISO2[uppercase] ?? (validCountryCodes.has(uppercase) ? uppercase : normalized);
};

/**
 * Returns true if the given string is a valid ISO 3166-1 alpha-2 country code.
 */
export const isValidCountryCode = (code: string): boolean => validCountryCodes.has(code);

// USPS postal regions are valid on saved addresses, but do not describe geographic places for area matching.
// https://pe.usps.com/text/pub28/28c2_010.htm
const US_POSTAL_REGIONS: ReadonlySet<string> = new Set(["AA", "AE", "AP"]);

/**
 * Resolves known region names/codes only within an explicit supported country.
 * A one-argument call retains the legacy casing behavior; unknown/foreign text is preserved with country context.
 */
export const normalizeRegion = (value: unknown, country?: string | null): string | null | undefined => {
  const normalized = normalizeRawString(value);
  if (normalized == null) {
    return normalized;
  }

  if (country === undefined) {
    return normalized.length <= 3 ? normalized.toUpperCase() : normalized;
  }
  const regionCountry = asRegionCountry(normalizeCountry(country) ?? null);
  if (!regionCountry) {
    return normalized;
  }
  const code = normalized.replace(/\./g, "").toUpperCase();
  if (regionCountry === "US" && US_POSTAL_REGIONS.has(code)) {
    return code;
  }
  const nameKey = normalized.toLowerCase().replace(/\./g, "").replace(/\s+/g, " ");
  const named = REGION_CODES.find(entry => entry.country === regionCountry && entry.name.replace(/\./g, "") === nameKey);
  return named?.code ?? regionsForCode(normalized.replace(/\./g, ""), regionCountry)[0]?.code ?? normalized;
};

const POSTAL_CODE_FORMATS: Record<RegionCountry, RegExp> = {
  US: /^\d{5}(?:-\d{4})?$/,
  // Canada Post letter restrictions, documented in Statistics Canada's PCCF reference guide:
  // https://www150.statcan.gc.ca/n1/pub/92-154-g/92-154-g2017001-eng.htm
  CA: /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] \d[ABCEGHJ-NPRSTV-Z]\d$/,
  MX: /^\d{5}$/,
};

/**
 * Formats recognizable Canadian postal codes and US ZIP+4 without changing unresolved or foreign text.
 * A one-argument call retains the legacy casing/spacing behavior.
 */
export const normalizePostalCode = (value: unknown, country?: string | null): string | null | undefined => {
  const normalized = normalizeRawString(value);
  if (normalized == null) {
    return normalized;
  }

  if (country === undefined) {
    return normalized.replace(/\s+/g, " ").toUpperCase();
  }
  const countryCode = normalizeCountry(country);
  if (countryCode === "US" && /^\d{9}$/.test(normalized)) {
    return `${normalized.slice(0, 5)}-${normalized.slice(5)}`;
  }
  if (countryCode === "CA") {
    const compact = normalized.replace(/\s+/g, "").toUpperCase();
    const formatted = `${compact.slice(0, 3)} ${compact.slice(3)}`;
    if (POSTAL_CODE_FORMATS.CA.test(formatted)) {
      return formatted;
    }
  }
  return normalized;
};

/**
 * City key for area comparison: lowercase, trimmed, internal spaces collapsed,
 * periods removed, and the words "saint" and "st" folded to "st".
 */
export const placeCityKey = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  const collapsed = value.trim().toLowerCase().replace(/\./g, "").replace(/\s+/g, " ").trim();
  if (!collapsed) {
    return null;
  }
  return collapsed.replace(/\b(?:saint|st)\b/g, "st");
};

export interface NormalizedPlace {
  countryCode: string | null;
  stateCode: string | null;
  cityKey: string | null;
}

const asRegionCountry = (code: string | null): RegionCountry | null => (code === "US" || code === "CA" || code === "MX" ? code : null);

/**
 * Normalizes a place for comparison. A blank country is filled only from a state
 * that is unambiguous across US, CA, and MX. Returns null when nothing was given.
 * Stored addresses are not rewritten; callers compare the result.
 */
export const normalizePlace = (input: { country?: string | null; state?: string | null; city?: string | null }): NormalizedPlace | null => {
  const countryGiven = normalizeRawString(input.country);
  const stateGiven = normalizeRawString(input.state);
  const cityGiven = normalizeRawString(input.city);
  if (!countryGiven && !stateGiven && !cityGiven) {
    return null;
  }
  const cityKey = placeCityKey(input.city);
  const countryRaw = normalizeCountry(input.country);
  let countryCode: string | null = null;
  if (typeof countryRaw === "string" && countryRaw.length > 0) {
    const upper = countryRaw.toUpperCase();
    countryCode = upper.length === 2 ? upper : null;
  }
  const stateRaw = input.state?.trim() ?? "";
  let stateCode: string | null = null;
  if (stateRaw) {
    const named = regionByName(stateRaw);
    const namedCountry = named ? asRegionCountry(named.country) : null;
    if (named && namedCountry && (!countryCode || named.country === countryCode)) {
      stateCode = named.code;
      countryCode = countryCode ?? named.country;
    } else if (stateRaw.length <= 3) {
      const code = stateRaw.replace(/\./g, "").toUpperCase();
      const matches = regionsForCode(code, asRegionCountry(countryCode));
      if (matches.length === 1 && matches[0]) {
        stateCode = matches[0].code;
        countryCode = countryCode ?? matches[0].country;
      }
    }
  }
  return { countryCode, stateCode, cityKey };
};

export interface AddressFields {
  city?: string | null;
  country?: string | null;
  state?: string | null;
  street1?: string | null;
  street2?: string | null;
  zip?: string | null;
}

export type AddressField = "country" | "state" | "zip";

export interface AddressIssue {
  field: AddressField;
  code: "invalid_country" | "invalid_region" | "invalid_postal_code";
  message: string;
}

/** Normalizes only present fields. A partial update can retain its explicitly stored country. */
export const normalizeAddressFields = <
  T extends {
    city?: unknown;
    country?: unknown;
    state?: unknown;
    street1?: unknown;
    street2?: unknown;
    zip?: unknown;
  },
>(
  input: T,
  previous?: AddressFields,
): T => {
  const output = { ...input };
  const country = normalizeCountry(input.country === undefined ? previous?.country : input.country) ?? null;

  if (Object.prototype.hasOwnProperty.call(output, "street1")) {
    output.street1 = normalizeRawString(output.street1);
  }
  if (Object.prototype.hasOwnProperty.call(output, "street2")) {
    output.street2 = normalizeRawString(output.street2);
  }
  if (Object.prototype.hasOwnProperty.call(output, "city")) {
    output.city = normalizeRawString(output.city);
  }
  if (Object.prototype.hasOwnProperty.call(output, "country")) {
    output.country = normalizeCountry(output.country);
  }
  if (Object.prototype.hasOwnProperty.call(output, "state")) {
    output.state = normalizeRegion(output.state, country);
  }
  if (Object.prototype.hasOwnProperty.call(output, "zip")) {
    output.zip = normalizePostalCode(output.zip, country);
  }

  return output;
};

/**
 * Frozen v1 Public API idempotency normalization. Never use this for saved address validation/writes.
 * Existing receipts hash these exact country aliases, region casing and postal spacing rules.
 */
export const normalizeAddressFieldsForLegacyIdentity = <
  T extends {
    city?: unknown;
    country?: unknown;
    state?: unknown;
    street1?: unknown;
    street2?: unknown;
    zip?: unknown;
  },
>(
  input: T,
): T => {
  const output = normalizeAddressFields(input);
  if (Object.prototype.hasOwnProperty.call(input, "country")) {
    const country = normalizeRawString(input.country);
    if (typeof country === "string") {
      output.country = COUNTRY_ALIAS_TO_ISO2[country.toUpperCase()] ?? country;
    } else {
      output.country = country;
    }
  }
  if (Object.prototype.hasOwnProperty.call(input, "state")) {
    output.state = normalizeRegion(input.state);
  }
  if (Object.prototype.hasOwnProperty.call(input, "zip")) {
    output.zip = normalizePostalCode(input.zip);
  }
  return output;
};

/**
 * Checks supplied country/region codes and supported postal formats, not address existence or city/postal compatibility.
 * Missing values are allowed; regions and postal codes outside US/CA/MX remain unchecked free text.
 */
export const getAddressIssues = (address: AddressFields): AddressIssue[] => {
  const normalized = normalizeAddressFields(address);
  const issues: AddressIssue[] = [];
  if (normalized.country && !isValidCountryCode(normalized.country)) {
    issues.push({
      field: "country",
      code: "invalid_country",
      message: "Select a recognized country or enter a valid two-letter country code.",
    });
  }
  const country = asRegionCountry(normalized.country ?? null);
  if (!country) {
    return issues;
  }
  if (
    normalized.state &&
    regionsForCode(normalized.state, country).length === 0 &&
    !(country === "US" && US_POSTAL_REGIONS.has(normalized.state))
  ) {
    issues.push({ field: "state", code: "invalid_region", message: `Enter a recognized state or province for ${country}.` });
  }
  if (normalized.zip && !POSTAL_CODE_FORMATS[country].test(normalized.zip)) {
    const format = country === "CA" ? "A1A 1A1" : country === "US" ? "12345 or 12345-6789" : "12345";
    issues.push({ field: "zip", code: "invalid_postal_code", message: `Enter a postal code in ${format} format for ${country}.` });
  }
  return issues;
};

/** Only changed fields (or a changed country dependency) block updates to legacy addresses. */
export const getBlockingAddressIssues = (next: AddressFields, previous?: AddressFields): AddressIssue[] => {
  const merged = normalizeAddressFields({
    country: next.country === undefined ? previous?.country : next.country,
    state: next.state === undefined ? previous?.state : next.state,
    zip: next.zip === undefined ? previous?.zip : next.zip,
  });
  const issues = getAddressIssues(merged);
  if (!previous) {
    return issues;
  }
  const prior = normalizeAddressFields(previous);
  const countryChanged = (merged.country ?? null) !== (prior.country ?? null);
  return issues.filter(issue => countryChanged || (merged[issue.field] ?? null) !== (prior[issue.field] ?? null));
};
